import { useState } from 'react'
import type { NewList } from '@domain/ports/list-repository'
import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'

/**
 * Criar uma lista nova.
 *
 * ## O ícone é ESCOLHIDO de uma fileira, e não digitado
 *
 * Ele é o que distingue as abas de relance — a de mercado da de farmácia — e um
 * campo de texto pedindo emoji é um teclado a mais no celular para um valor de
 * um caractere. A fileira cobre o que uma casa cria de verdade; quem quiser
 * outro cola no campo do nome, que aceita qualquer coisa.
 *
 * ## Só o nome é obrigatório
 *
 * Sem ícone a aba mostra o nome, e um nome sozinho já distingue. Exigir os dois
 * seria fazer a pessoa escolher um desenho para poder guardar uma ideia.
 */
const ICONES = ['🛒', '💊', '🏠', '🐾', '🎁', '🔧', '📦', '🍺']

export function FolhaDeLista({
  onFechar,
  onCriar,
}: {
  onFechar: () => void
  onCriar: (nova: NewList) => Promise<void>
}) {
  const [nome, setNome] = useState('')
  const [icone, setIcone] = useState(ICONES[0] ?? '🛒')
  const [gravando, setGravando] = useState(false)
  const [falhou, setFalhou] = useState(false)

  const pode = nome.trim() !== '' && !gravando

  return (
    <FolhaDeBaixo titulo="Nova lista" onFechar={onFechar}>
      <h2 className="titulo mb-3 text-base">Nova lista</h2>

      <label className="block text-sm">
        <span className="mb-1 block text-xs text-[var(--text-muted)]">Nome</span>
        <input
          value={nome}
          aria-label="Nome da lista"
          placeholder="Farmácia"
          // 16px: abaixo disso o Safari do iPhone dá zoom ao focar.
          className="w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-base"
          onChange={(e) => {
            setNome(e.target.value)
          }}
        />
      </label>

      <fieldset className="mt-3">
        <legend className="text-xs text-[var(--text-muted)]">Ícone</legend>
        <div className="flex flex-wrap gap-2">
          {ICONES.map((x) => (
            <button
              key={x}
              type="button"
              role="radio"
              aria-checked={x === icone}
              aria-label={`Ícone ${x}`}
              onClick={() => {
                setIcone(x)
              }}
              className={`alvo w-11 rounded-full border px-0 text-lg ${
                x === icone ? 'border-[var(--brand)]' : 'border-[var(--border)]'
              }`}
            >
              {x}
            </button>
          ))}
        </div>
      </fieldset>

      {falhou && (
        <p role="alert" className="mt-3 text-xs text-[var(--text-muted)]">
          Não foi possível criar agora.
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Botao
          type="button"
          tom="primario"
          disabled={!pode}
          onClick={() => {
            setGravando(true)
            setFalhou(false)
            // `kind: 'shopping'` sempre: `reminders` existe no tipo e nenhuma
            // tela o distingue hoje — oferecer a escolha seria pedir uma decisão
            // que não muda nada.
            void onCriar({ name: nome.trim(), kind: 'shopping', icon: icone })
              .then(onFechar)
              .catch(() => {
                setFalhou(true)
                setGravando(false)
              })
          }}
        >
          Criar a lista
        </Botao>
        <Botao type="button" onClick={onFechar}>
          Cancelar
        </Botao>
      </div>
    </FolhaDeBaixo>
  )
}
